import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar, SnackbarCloseReason, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useDebounce } from '../../../shared/hooks';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/ContaBancaria';
import { LancamentoEntreConstasService } from '../../../shared/services/api/Financeiro/LancamentoEntreConstas';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';

// Destino é sempre 'banco' ou 'caixa' — nunca 'recebiveis' (recebível só
// entra por liquidação de maquininha, ver DialogTransferirRecebivel) — por
// isso a restrição "nunca pra recebíveis" já vem de graça, sem checagem
// extra: essas são as únicas duas opções que o menu oferece.
export type TDestinoTransferenciaConta = 'banco' | 'caixa';
// Conta banco e conta cofre podem originar as duas transferências, só muda o
// texto (ver TITULOS abaixo). Cofre nunca é destino — só origem.
export type TOrigemTransferenciaConta = 'banco' | 'cofre';

export interface DialogTransferenciaContaProps {
  open: boolean;
  destino: TDestinoTransferenciaConta;
  tipoContaOrigem: TOrigemTransferenciaConta;
  idcontaOrigem: string;
  nomeContaOrigem?: string;
  // Dia pesquisado na Conciliação (AAAA-MM-DD) — a data já abre nele, pra
  // transferência cair no caixa daquele dia. Sem ele, hoje.
  dataPadrao?: string;
  onClose: (value?: string) => void;
}

type TContaOpcao = { idconta: string; label: string };

// Título/histórico mudam conforme quem origina (banco ou cofre) e pra onde
// vai (outra conta banco ou um caixa físico). O histórico serve tanto pra
// reverter (handleVoltar em Conciliacao.tsx) quanto pra classificar a linha
// na coluna Categoria (ver extrairCategoriaDoHistorico) — por isso fica
// sempre com o mesmo texto pra cada combinação.
const TITULOS: Record<TOrigemTransferenciaConta, Record<TDestinoTransferenciaConta, string>> = {
  banco: { banco: 'Transferência entre Contas', caixa: 'Saque Bancário' },
  cofre: { banco: 'Transferência para Conta Bancária', caixa: 'Transferência do Cofre para Caixa' },
};

const LABEL_CONTA_DESTINO: Record<TDestinoTransferenciaConta, string> = {
  banco: 'Conta de destino',
  caixa: 'Caixa de destino',
};

export const DialogTransferenciaConta: React.FC<DialogTransferenciaContaProps> = ({
  open, destino, tipoContaOrigem, idcontaOrigem, nomeContaOrigem, dataPadrao, onClose,
}) => {
  const { debounce } = useDebounce();
  const config = {
    titulo: TITULOS[tipoContaOrigem][destino],
    tipoContaDestino: destino,
    labelContaDestino: LABEL_CONTA_DESTINO[destino],
    historicoBase: TITULOS[tipoContaOrigem][destino],
  };

  const [busca, setBusca] = useState('');
  const [opcoesConta, setOpcoesConta] = useState<TContaOpcao[]>([]);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [contaDestino, setContaDestino] = useState<TContaOpcao | null>(null);

  const [data, setData] = useState(dataPadrao || dayjs().format('YYYY-MM-DD'));
  const [valorTexto, setValorTexto] = useState('');
  const [observacao, setObservacao] = useState('');

  const [transferindo, setTransferindo] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  useEffect(() => {
    if (!open) return;
    setBusca('');
    setContaDestino(null);
    setData(dataPadrao || dayjs().format('YYYY-MM-DD'));
    setValorTexto('');
    setObservacao('');
  }, [open, destino, tipoContaOrigem, dataPadrao]);

  useEffect(() => {
    if (!open) return;
    setCarregandoContas(true);
    debounce(() => {
      ContaBancariaService.getAllList(busca, '', config.tipoContaDestino, 20).then((resultado) => {
        setCarregandoContas(false);
        if (resultado instanceof Error) { setOpcoesConta([]); return; }
        setOpcoesConta(
          resultado.data
            .filter((conta) => conta.idconta !== idcontaOrigem)
            .map((conta) => ({ idconta: conta.idconta, label: conta.titular })),
        );
      });
    });
  }, [open, busca, config.tipoContaDestino, idcontaOrigem, debounce]);

  const valor = valorTexto ? Number(valorTexto) / 100 : 0;
  const exibir = (v: number) => (v === 0 ? '' : formatarMoeda(v));
  const handleChangeValor = (texto: string) => setValorTexto(texto.replace(/\D/g, ''));

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    if (tms === 'success') onClose('true');
  };

  const confirmar = () => {
    if (!contaDestino) {
      setTms('warning');
      setMsg(`Selecione ${config.tipoContaDestino === 'caixa' ? 'o caixa' : 'a conta'} de destino`);
      setOpenmsg(true);
      return;
    }
    if (valor <= 0) {
      setTms('warning');
      setMsg('Informe o valor');
      setOpenmsg(true);
      return;
    }

    const historico = observacao ? `${config.historicoBase} - ${observacao}` : config.historicoBase;

    setTransferindo(true);
    LancamentoEntreConstasService
      .create({
        identrada: contaDestino.idconta,
        idsaida: idcontaOrigem,
        valor,
        historico,
        numero: '',
        datalan: dayjs(data, 'YYYY-MM-DD').toDate(),
      })
      .then((resultado) => {
        setTransferindo(false);
        if (resultado instanceof Error) {
          setTms('error');
          setMsg(resultado.message);
          setOpenmsg(true);
          return;
        }
        setTms('success');
        setMsg(`${config.titulo} registrada com sucesso!`);
        setOpenmsg(true);
      });
  };

  const handleCancel = () => onClose('false');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{config.titulo}{nomeContaOrigem ? ` — ${nomeContaOrigem}` : ''}</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2500} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {config.labelContaDestino}
        </Typography>
        <Autocomplete
          openText='Abrir'
          closeText='Fechar'
          noOptionsText='Nenhuma conta encontrada'
          loadingText='Carregando...'
          disablePortal
          options={opcoesConta}
          loading={carregandoContas}
          disabled={transferindo}
          value={contaDestino}
          onInputChange={(_, newValue) => setBusca(newValue)}
          onChange={(_, newValue) => setContaDestino(newValue)}
          popupIcon={carregandoContas ? <CircularProgress size={20} /> : undefined}
          renderInput={(params) => (
            <TextField {...params} size='small' label={config.labelContaDestino} />
          )}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Valor"
            placeholder="0,00"
            value={exibir(valor)}
            onChange={(e) => handleChangeValor(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
            disabled={transferindo}
          />
          <TextField
            size="small"
            type="date"
            label="Data"
            InputLabelProps={{ shrink: true }}
            value={data}
            inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
            onChange={(e) => setData(e.target.value)}
            disabled={transferindo}
            sx={{ minWidth: 150 }}
          />
        </Box>

        {/* Dia anterior: o lançamento cai no caixa desse dia, e o histórico
            registra quando a transferência foi feita (ver
            anotarTransferenciaRetroativa, backend). */}
        {data && data < dayjs().format('YYYY-MM-DD') && (
          <Alert severity="info" sx={{ mb: 2 }}>
            O lançamento cai no caixa de <b>{dayjs(data).format('DD/MM/YYYY')}</b>. No histórico fica registrado que a
            transferência foi feita em {dayjs().format('DD/MM/YYYY [às] HH:mm')}.
          </Alert>
        )}
        <TextField
          fullWidth
          size="small"
          label="Observação"
          placeholder="Opcional"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          disabled={transferindo}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleCancel} disabled={transferindo}>Cancelar</Button>
        <Button variant="outlined" autoFocus disabled={transferindo} onClick={confirmar}>
          {transferindo ? 'Transferindo...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
